import { decode } from 'html-entities';

/**
 * Decodes HTML entities in a string.
 * Handles decimal entities (&#123;), hexadecimal entities (&#x1F;), and named entities.
 * Uses the html-entities library for robust, standards-compliant decoding.
 */
export const decodeHtmlEntities = (text: string): string => {
    if (!text) {
        return text;
    }

    return decode(text);
};

/**
 * Formats a potentially comma-separated list by:
 * 1. Decoding HTML entities
 * 2. Treating text wrapped in square brackets as an array list and formatting it as a comma-separated list
 */
export const formatCommaList = (text: string): string => {
    if (!text) {
        return text;
    }

    const decodedText = decodeHtmlEntities(text).trim();

    // If the text is wrapped in square brackets, treat it as an array list and format it as a comma-separated list
    if (decodedText.startsWith('[') && decodedText.endsWith(']')) {
        const withoutBrackets = removeBrackets(decodedText);
        return withoutBrackets
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
            .join(', ');
    }

    return decodedText;
};

/**
 * Removes square brackets from the start and end of a string.
 * Only removes brackets if they are at the very beginning and end.
 */
export const removeBrackets = (text: string): string => {
    if (!text) {
        return text;
    }

    const trimmed = text.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        return trimmed.slice(1, -1).trim();
    }
    return trimmed;
};

/**
 * Removes duplicate values from a comma-separated string.
 * Splits by comma, trims each part, filters empty strings, deduplicates, then joins back with commas.
 */
export const removeDuplicates = (value: string): string => {
    if (!value) {
        return value;
    }

    const unique = Array.from(
        new Set(
            value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
        ),
    );

    return unique.join(', ');
};
